import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Mail, RefreshCw, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function VerifyEmailPending() {
  const location = useLocation();
  const email = location.state?.email || '';
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResend = async () => {
    if (!email) {
      toast.error('Email address not found. Please try logging in again.');
      return;
    }
    
    setResending(true);
    try {
      await api.post('/auth/resend-verification', null, { params: { email } });
      setResent(true);
      toast.success('Verification email sent!');
    } catch (err) {
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16" data-testid="verify-email-pending-page">
      <SEO 
        title="Verify Your Email"
        description="Please verify your email address to complete your registration."
        pathname="/verify-email-pending"
        noindex={true}
      />
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mb-6">
          <Mail className="h-8 w-8 text-stone-600" />
        </div>
        
        {/* Content */}
        <h1 className="text-2xl md:text-3xl font-medium mb-4">Check your email</h1>
        <p className="text-stone-600 mb-2">
          We&apos;ve sent a verification link to:
        </p>
        <p className="font-medium text-stone-900 mb-6">{email || 'your email address'}</p>
        
        <p className="text-sm text-stone-500 mb-8">
          Click the link in the email to verify your account. If you don&apos;t see it, check your spam folder.
        </p>
        
        {/* Actions */}
        <div className="space-y-4">
          {resent ? (
            <div className="flex items-center justify-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span>Verification email sent!</span>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={handleResend}
              disabled={resending}
              className="w-full"
              data-testid="resend-verification-button"
            >
              {resending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Resend verification email
                </>
              )}
            </Button>
          )}
          
          <Link to="/login" className="block">
            <Button variant="ghost" className="w-full">
              Back to Sign In
            </Button>
          </Link>
        </div>
        
        {/* Help text */}
        <p className="text-xs text-stone-400 mt-8">
          Wrong email?{' '}
          <Link to="/login" className="underline hover:text-stone-600">
            Sign up with a different email
          </Link>
        </p>
      </div>
    </div>
  );
}
